import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, BadgeCheck, Globe2, HeartPulse, Languages, PiggyBank, SunMedium, Wifi } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CountryLabel } from "@/components/CountryLabel";
import { CountrySafetySignal } from "@/components/CountrySafetySignal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getCountryPreviewContent } from "@/lib/countryPreviewContent";
import { filterDistinctIdeaList } from "@/lib/reasoningQuality";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const signalIcons = {
  "Affordability": PiggyBank,
  "Healthcare": HeartPulse,
  "Residency Accessibility": BadgeCheck,
  "Language / English Ease": Languages,
  "Infrastructure / Internet": Wifi,
  "Lifestyle / Climate": SunMedium,
};

const publicFitFactors = [
  "Healthcare practicality",
  "Affordability",
  "Climate",
  "Residency practicality",
  "Infrastructure",
  "Lifestyle preferences",
];

const publicExperienceFactors = [
  "Daily life",
  "Healthcare access",
  "Infrastructure",
  "Housing pressure",
  "Residency practicality",
  "Regional differences",
];

const regionalDifferenceFactors = [
  "Healthcare access",
  "Housing pressure",
  "Climate",
  "Infrastructure",
  "Expat familiarity",
];

const buildCountryPreviewPrimaryLabel = (preview) => {
  const options = [
    `See if ${preview.name} matches your profile`,
    `Compare ${preview.name} against your priorities`,
    `Find out if ${preview.name} fits your relocation goals`,
  ];
  return options[(preview.name.length + preview.code.length) % options.length];
};

const CountryPreviewPage = ({ countryKey }) => {
  const preview = getCountryPreviewContent(countryKey);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [safetyProfile, setSafetyProfile] = useState(null);
  const previewCode = preview?.code || "";

  useEffect(() => {
    if (!previewCode) {
      setSafetyProfile(null);
      return undefined;
    }

    let isActive = true;

    const fetchCountrySafety = async () => {
      try {
        const response = await axios.get(`${API}/countries/${previewCode}/public-meta`);
        if (isActive) {
          setSafetyProfile(response.data?.safety_profile || null);
        }
      } catch (error) {
        if (isActive) {
          setSafetyProfile(null);
        }
      }
    };

    fetchCountrySafety();
    return () => {
      isActive = false;
    };
  }, [previewCode]);

  if (!preview) {
    return <Navigate to="/" replace />;
  }

  const activeImage = preview.images[currentSlide];
  const goToPreviousSlide = () => setCurrentSlide((currentSlide - 1 + preview.images.length) % preview.images.length);
  const goToNextSlide = () => setCurrentSlide((currentSlide + 1) % preview.images.length);
  const visibleWhyIncluded = filterDistinctIdeaList(preview.whyIncluded, [], preview.whyIncluded.length);
  const contextualPrimaryLabel = buildCountryPreviewPrimaryLabel(preview);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <section className="border-b border-stone-200/80 bg-[linear-gradient(180deg,#f8f6f0_0%,#fdfcf8_50%,#faf7f2_100%)]">
          <div className="rc-container py-10 sm:py-14 lg:py-16">
            <div className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
              <div className="space-y-6" data-testid="country-preview-hero-copy">
                <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/90 px-4 py-2 text-xs font-medium uppercase tracking-[0.24em] text-stone-500" data-testid="country-preview-kicker">
                  Explore Country Preview
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-stone-500 transition-[color,transform] duration-200 hover:-translate-x-0.5 hover:text-stone-800"
                  data-testid="country-preview-back-home-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Home</span>
                </Link>
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <CountryLabel
                      code={preview.code}
                      name={preview.name}
                      size={22}
                      className="text-stone-900"
                      textClassName="font-manrope text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
                      flagTestId={`country-preview-flag-${preview.code}`}
                      dataTestId={`country-preview-title-${preview.slug}`}
                    />
                  </div>
                  <p className="text-base text-stone-600 sm:text-lg" data-testid="country-preview-subtitle">
                    {preview.subtitle}
                  </p>
                  <p className="max-w-2xl text-base leading-8 text-stone-700" data-testid="country-preview-intro">
                    {preview.intro}
                  </p>
                  <CountrySafetySignal
                    safetyProfile={safetyProfile}
                    variant="callout"
                    showContext
                    dataTestIdPrefix={`country-preview-safety-signal-${preview.code}`}
                  />
                </div>

                <div className="flex flex-wrap gap-3" data-testid="country-preview-highlight-chips">
                  {preview.heroHighlights.map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-stone-200 bg-stone-50/85 px-4 py-2 text-sm text-stone-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild className="h-12 rounded-full bg-stone-950 px-7 text-sm font-medium text-stone-50 transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-black" data-testid="country-preview-primary-cta">
                    <Link to={preview.cta.primaryHref}>
                      {contextualPrimaryLabel}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  {preview.cta.secondaryLabel && preview.cta.secondaryHref ? (
                    <Button asChild variant="outline" className="rc-button-outline h-12 rounded-full px-7 text-sm" data-testid="country-preview-secondary-cta">
                      <Link to={preview.cta.secondaryHref}>{preview.cta.secondaryLabel}</Link>
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[2rem] border border-stone-200/90 bg-white/92 p-4 shadow-[0_20px_50px_rgba(28,25,23,0.08)]" data-testid="country-preview-hero-carousel-shell">
                <div className="relative overflow-hidden rounded-[1.65rem] border border-stone-200/90 bg-stone-100">
                  <div className="aspect-[4/3] w-full">
                    <img
                      src={activeImage.url}
                      alt={activeImage.alt}
                      className="h-full w-full object-cover"
                      data-testid={`country-preview-image-${currentSlide + 1}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={goToPreviousSlide}
                    className="absolute left-4 bottom-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white/95 text-stone-900 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white"
                    data-testid="country-preview-carousel-prev"
                    aria-label="Previous image"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={goToNextSlide}
                    className="absolute right-4 bottom-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-200 bg-white/95 text-stone-900 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:bg-white"
                    data-testid="country-preview-carousel-next"
                    aria-label="Next image"
                  >
                    →
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3 px-2 pt-4">
                  <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500" data-testid={`country-preview-image-label-${currentSlide + 1}`}>
                    {activeImage.label}
                  </span>
                  <span className="text-xs text-stone-500" data-testid="country-preview-image-position">
                    {currentSlide + 1} / {preview.images.length}
                  </span>
                </div>

                <div className="mt-4 flex items-center gap-2 px-2" data-testid="country-preview-carousel-dots">
                  {preview.images.map((image, index) => (
                    <button
                      key={image.url}
                      type="button"
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ${currentSlide === index ? "w-8 bg-stone-900" : "w-2.5 bg-stone-300 hover:bg-stone-400"}`}
                      data-testid={`country-preview-carousel-dot-${index + 1}`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rc-home-section">
          <div className="rc-container space-y-8">
            <div className="max-w-3xl space-y-4" data-testid="country-preview-why-included-section">
              <p className="rc-section-kicker">Model Relevance</p>
              <h2 className="rc-section-title text-left">Why {preview.name} Makes Our Curated List</h2>
              <div className="space-y-4 text-base leading-8 text-stone-700">
                {visibleWhyIncluded.map((paragraph, index) => (
                  <p key={paragraph} data-testid={`country-preview-why-included-paragraph-${index + 1}`}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="country-preview-snapshot-grid">
              {preview.snapshotSignals.map((signal) => {
                const Icon = signalIcons[signal.label] || Globe2;

                return (
                  <Card key={signal.label} className="rc-subtle-card border-stone-200/85 bg-white/90">
                    <CardHeader className="space-y-4 p-6">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-500">
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500" data-testid={`country-preview-snapshot-signal-${signal.label}`}>
                          {signal.signal}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="font-manrope text-xl text-stone-900">{signal.label}</CardTitle>
                        <CardDescription className="text-sm leading-7 text-stone-600">
                          {signal.detail}
                        </CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-preview-best-fit-section">
              <CardHeader className="p-7 pb-5">
                <CardTitle className="font-manrope text-2xl text-stone-900">How Relocation Clarity evaluates fit</CardTitle>
                <CardDescription className="text-sm leading-7 text-stone-600">
                  Country fit changes based on priorities like healthcare, affordability, climate, residency practicality, infrastructure, and lifestyle preferences.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-7 pb-7 pt-0">
                <div className="flex flex-wrap gap-3">
                  {publicFitFactors.map((item, index) => (
                    <span
                      key={item}
                      className="rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-sm text-stone-700"
                      data-testid={`country-preview-best-fit-chip-${index + 1}`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to="/assessment"
                  className="mt-5 inline-flex text-sm font-medium text-amber-700 underline-offset-4 transition-colors duration-200 hover:text-amber-800 hover:underline"
                  data-testid="country-preview-fit-transition-cta"
                >
                  See how this country matches your priorities →
                </Link>
              </CardContent>
            </Card>

            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-preview-tradeoffs-section">
              <CardHeader className="p-7 pb-5">
                <CardTitle className="font-manrope text-2xl text-stone-900">What changes the relocation experience</CardTitle>
                <CardDescription className="text-sm leading-7 text-stone-600">
                  Daily life, healthcare access, infrastructure, housing pressure, residency practicality, and regional differences can change how a country feels long term.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-7 pb-7 pt-0">
                <div className="flex flex-wrap gap-3">
                  {publicExperienceFactors.map((item, index) => (
                    <span
                      key={item}
                      className="rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-sm text-stone-700"
                      data-testid={`country-preview-tradeoff-${index + 1}`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to="/assessment"
                  className="mt-5 inline-flex text-sm font-medium text-amber-700 underline-offset-4 transition-colors duration-200 hover:text-amber-800 hover:underline"
                  data-testid="country-preview-experience-transition-cta"
                >
                  See how these factors apply to your profile →
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section pt-0">
          <div className="rc-container space-y-6">
            <div className="max-w-2xl space-y-3" data-testid="country-preview-regions-header">
              <p className="rc-section-kicker">Location Reality</p>
              <h2 className="rc-section-title text-left" data-testid="country-preview-location-variability-title">Why location choice changes the experience</h2>
              <p className="text-base leading-8 text-stone-600" data-testid="country-preview-location-variability-description">
                A country can feel very different from one city to the next. Healthcare access, housing pressure, climate, infrastructure, and expat familiarity often shift locally, so country-level assumptions rarely tell the full day-to-day story.
              </p>
            </div>

            <Card className="rc-subtle-card border-stone-200/85 bg-white/90" data-testid="country-preview-regions-grid">
              <CardContent className="p-7 space-y-5">
                <div className="flex flex-wrap gap-3">
                  {regionalDifferenceFactors.map((item, index) => (
                    <span
                      key={item}
                      className="rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-sm text-stone-700"
                      data-testid={`country-preview-region-name-${index + 1}`}
                    >
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to="/assessment"
                  className="inline-flex text-sm font-medium text-amber-700 underline-offset-4 transition-colors duration-200 hover:text-amber-800 hover:underline"
                  data-testid="country-preview-regional-differences-cta"
                >
                  See how location choice reshapes the fit →
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="rc-home-section-ending pt-0">
          <div className="rc-container">
            <div className="rounded-[2rem] border border-stone-200/90 bg-[linear-gradient(135deg,rgba(251,250,247,0.96)_0%,rgba(247,243,235,0.96)_100%)] p-8 shadow-[0_16px_34px_rgba(28,25,23,0.06)] sm:p-10" data-testid="country-preview-cta-band">
              <div className="max-w-3xl space-y-4">
                <p className="rc-section-kicker">{preview.cta.eyebrow}</p>
                <h2 className="font-manrope text-3xl text-stone-900 sm:text-4xl" data-testid="country-preview-cta-title">
                  {preview.cta.title}
                </h2>
                <p className="text-base leading-8 text-stone-600" data-testid="country-preview-cta-description">
                  {preview.cta.description}
                </p>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild className="h-12 rounded-full bg-stone-950 px-7 text-sm font-medium text-stone-50 transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-black" data-testid="country-preview-bottom-primary-cta">
                  <Link to={preview.cta.primaryHref}>
                    {contextualPrimaryLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                {preview.cta.secondaryLabel && preview.cta.secondaryHref ? (
                  <Button asChild variant="outline" className="rc-button-outline h-12 rounded-full px-7 text-sm" data-testid="country-preview-bottom-secondary-cta">
                    <Link to={preview.cta.secondaryHref}>{preview.cta.secondaryLabel}</Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CountryPreviewPage;